import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PaginationSetting } from '@grid/abstractions';
import { paginationOptions } from '@shared/constants/pagination-settings';

@Component({
  standalone: false,
  selector: 'gstar-grid-footer',
  templateUrl: './grid-footer.component.html',
  styleUrls: ['./grid-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridFooterComponent<TDATA> implements OnInit, OnChanges {
  @Input() data!: TDATA[];
  @Input() paginationSetting!: PaginationSetting;
  @Input() options: KeyValue<string, number>[] = [];
  @Input() extendedPagination = false;
  @Output() pageChange: EventEmitter<PaginationSetting> = new EventEmitter();

  isFirst = true;

  ngOnInit(): void {
    if (this.extendedPagination) {
      this.normalizePaginationSetting();
      return;
    }

    this.paginationSetting = {
      ...this.paginationSetting,
    };

    if (this.options.length === 0) {
      this.options = paginationOptions;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.extendedPagination && changes['paginationSetting']) {
      this.normalizePaginationSetting();
    }
  }

  get rowByPage(): number {
    return this.getPageSize(this.paginationSetting?.rowByPage);
  }

  get currentPage(): number {
    if (this.paginationSetting.totalItems === 0) {
      return 0;
    }

    if (this.extendedPagination) {
      return this.getCurrentPage(this.paginationSetting.currentPage);
    }

    return this.paginationSetting.currentPage;
  }

  get totalPages(): number {
    const rowsPerPage = this.extendedPagination
      ? this.rowByPage
      : (this.paginationSetting.rowByPage as number);

    return Math.ceil(this.paginationSetting.totalItems / rowsPerPage);
  }

  get currentRows(): string {
    const rowsCalc = this.extendedPagination
      ? this.rowByPage * this.currentPage
      : this.paginationSetting.rowByPage * this.paginationSetting.currentPage;
    const start = this.startPageItem();
    const end = this.endPageItem(rowsCalc);

    return `${start} - ${end}`;
  }

  select(event: Event): void {
    const control = event.target as HTMLSelectElement;
    const rowsByPage = parseInt(control.value, 10);
    this.paginationSetting.rowByPage = rowsByPage;
    this.validate();
    this.pageChange.emit({ ...this.paginationSetting });
  }

  onPageSizeChange(rowsByPage: number): void {
    const normalizedPageSize = this.getPageSize(rowsByPage);

    if (!this.isValidPageSize(normalizedPageSize)) {
      return;
    }

    this.paginationSetting = {
      ...this.paginationSetting,
      rowByPage: normalizedPageSize,
      currentPage: 1,
    };
    this.validate();
    this.emitPageChange();
  }

  changePage(pageNumber: number): void {
    if (this.extendedPagination) {
      this.paginationSetting = {
        ...this.paginationSetting,
        currentPage: this.getCurrentPage(pageNumber),
      };
    } else {
      this.paginationSetting.currentPage = pageNumber < 1 ? 1 : pageNumber;
    }

    this.pageChange.emit({
      ...this.paginationSetting,
      isFirst: this.isFirst,
    });

    if (this.isFirst) {
      this.isFirst = false;
    }
  }

  goToPage(pageNumber: number): void {
    if (this.paginationSetting?.currentPage) {
      this.paginationSetting.currentPage = this.extendedPagination
        ? this.getCurrentPage(pageNumber)
        : pageNumber;
    }
  }

  private emitPageChange(): void {
    const paginationWithoutIsFirst = { ...this.paginationSetting };
    delete paginationWithoutIsFirst.isFirst;
    this.pageChange.emit({ ...paginationWithoutIsFirst });
  }

  private normalizePaginationSetting(): void {
    if (!this.paginationSetting) {
      return;
    }

    this.paginationSetting = {
      ...this.paginationSetting,
      rowByPage: this.getPageSize(this.paginationSetting.rowByPage),
      currentPage: this.getCurrentPage(this.paginationSetting.currentPage),
    };
  }

  private getPageSize(value: number | string | undefined): number {
    const pageSize = Number(value);

    return Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 10;
  }

  private getCurrentPage(value: number | string | undefined): number {
    const currentPage = Number(value);

    return Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1;
  }

  private isValidPageSize(pageSize: number): boolean {
    return this.options.some(option => option.value === pageSize);
  }

  private validate(): void {
    if (this.extendedPagination && this.paginationSetting.totalItems <= 0) {
      this.paginationSetting.currentPage = 1;
      return;
    }

    if (this.paginationSetting.currentPage > this.totalPages) {
      this.paginationSetting.currentPage = this.totalPages;
    }

    if (this.extendedPagination && this.paginationSetting.currentPage < 1) {
      this.paginationSetting.currentPage = 1;
    }
  }

  private startPageItem(): number {
    const rowsPerPage = this.extendedPagination
      ? this.rowByPage
      : this.paginationSetting.rowByPage;
    const page = this.extendedPagination
      ? this.currentPage
      : this.paginationSetting.currentPage;

    return this.paginationSetting.totalItems >= 1
      ? rowsPerPage * page - (rowsPerPage - 1)
      : 0;
  }

  private endPageItem(rowsCalc: number): number {
    return rowsCalc > this.paginationSetting.totalItems
      ? this.paginationSetting.totalItems
      : rowsCalc;
  }
}
